<template>
  <div class="w-full max-w-md">
    <div class="text-center mb-8">
      <h1 class="font-bold text-2xl text-foreground">{{ tt('豚鼠', '豚鼠') }}</h1>
      <p class="mt-2 text-muted-foreground">{{ tt('创建账户，加入阅读社区', '建立帳戶，加入閱讀社區') }}</p>
    </div>

    <div class="rounded-[24px] border border-border/80 bg-card p-6 shadow-card">
      <h2 class="text-2xl font-bold text-center text-foreground">{{ tt('注册', '註冊') }}</h2>
      <p class="mt-1 text-center text-sm text-muted-foreground">{{ tt('填写姓名、邮箱和密码创建账户', '填寫姓名、郵箱和密碼建立帳戶') }}</p>

      <div class="mt-6" data-testid="register-form">
        <div
          v-if="pendingVerification"
          class="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950"
          role="status"
        >
          <p class="font-medium">{{ tt('去邮箱打开链接', '去郵箱打開連結') }}</p>
          <p v-if="passwordIgnored" class="mt-1">{{ tt('这次填写的新密码没有生效', '這次填寫的新密碼沒有生效') }}</p>
        </div>

        <div
          v-if="errorMessage"
          class="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          {{ errorMessage }}
          <p v-if="emailTaken" class="mt-2">
            <a :href="loginHref" class="font-medium underline">{{ tt('去登录', '去登入') }}</a>
            <span class="mx-1">·</span>
            <a :href="getLocalePrefix() + '/auth/forgot-password'" class="font-medium underline">{{ tt('找回密码', '找回密碼') }}</a>
          </p>
        </div>

        <form class="space-y-4" @submit.prevent="handleRegister">
          <div class="space-y-2">
            <label for="name" class="text-sm font-medium text-foreground">{{ tt('姓名', '姓名') }}</label>
            <input
              id="name"
              v-model="formData.name"
              type="text"
              autocomplete="name"
              required
              :placeholder="tt('请输入姓名', '請輸入姓名')"
              :class="
                fieldErrors.name
                  ? 'h-11 w-full rounded-xl border border-red-300 bg-background px-3 text-sm focus:border-red-500 focus:outline-none'
                  : 'h-11 w-full rounded-xl border border-border bg-background px-3 text-sm'
              "
              @input="clearFieldError('name')"
            />
            <p v-if="fieldErrors.name" class="text-sm text-red-600">{{ fieldErrors.name }}</p>
          </div>

          <div class="space-y-2">
            <label for="email" class="text-sm font-medium text-foreground">{{ tt('邮箱', '郵箱') }}</label>
            <input
              id="email"
              v-model="formData.email"
              type="email"
              autocomplete="email"
              required
              :placeholder="tt('请输入邮箱', '請輸入郵箱')"
              :class="
                fieldErrors.email
                  ? 'h-11 w-full rounded-xl border border-red-300 bg-background px-3 text-sm focus:border-red-500 focus:outline-none'
                  : 'h-11 w-full rounded-xl border border-border bg-background px-3 text-sm'
              "
              @input="clearFieldError('email')"
            />
            <p v-if="fieldErrors.email" class="text-sm text-red-600">{{ fieldErrors.email }}</p>
          </div>

          <div class="space-y-2">
            <label for="password" class="text-sm font-medium text-foreground">{{ tt('密码', '密碼') }}</label>
            <div class="relative">
              <input
                id="password"
                v-model="formData.password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password"
                required
                minlength="8"
                :placeholder="tt('请输入密码（至少8位）', '請輸入密碼（至少8位）')"
                :class="
                  fieldErrors.password
                    ? 'h-11 w-full rounded-xl border border-red-300 bg-background px-3 pr-10 text-sm focus:border-red-500 focus:outline-none'
                    : 'h-11 w-full rounded-xl border border-border bg-background px-3 pr-10 text-sm'
                "
                @input="clearFieldError('password')"
              />
              <button
                type="button"
                class="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                :aria-label="showPassword ? tt('隐藏密码', '隱藏密碼') : tt('显示密码', '顯示密碼')"
                @click="showPassword = !showPassword"
              >
                <svg
                  v-if="showPassword"
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" x2="23" y1="1" y2="23" />
                </svg>
                <svg
                  v-else
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>
            <div v-if="formData.password" class="mt-2 space-y-1">
              <div
                v-for="(req, index) in passwordRequirements"
                :key="index"
                class="flex items-center gap-2 text-xs"
              >
                <svg
                  class="h-3 w-3"
                  :class="req.met ? 'text-green-500' : 'text-gray-300'"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span :class="req.met ? 'text-green-600' : 'text-gray-500'">{{ req.text }}</span>
              </div>
            </div>
            <p v-if="fieldErrors.password" class="text-sm text-red-600">{{ fieldErrors.password }}</p>
          </div>

          <div class="space-y-2">
            <label for="confirmPassword" class="text-sm font-medium text-foreground">{{ tt('确认密码', '確認密碼') }}</label>
            <div class="relative">
              <input
                id="confirmPassword"
                v-model="formData.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                autocomplete="new-password"
                required
                :placeholder="tt('请再次输入密码', '請再次輸入密碼')"
                :class="
                  fieldErrors.confirmPassword
                    ? 'h-11 w-full rounded-xl border border-red-300 bg-background px-3 pr-10 text-sm focus:border-red-500 focus:outline-none'
                    : 'h-11 w-full rounded-xl border border-border bg-background px-3 pr-10 text-sm'
                "
                @input="clearFieldError('confirmPassword')"
              />
              <button
                type="button"
                class="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                :aria-label="showConfirmPassword ? tt('隐藏确认密码', '隱藏確認密碼') : tt('显示确认密码', '顯示確認密碼')"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <svg
                  v-if="showConfirmPassword"
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" x2="23" y1="1" y2="23" />
                </svg>
                <svg
                  v-else
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>
            <p v-if="fieldErrors.confirmPassword" class="text-sm text-red-600">
              {{ fieldErrors.confirmPassword }}
            </p>
          </div>

          <button
            type="submit"
            class="inline-flex h-11 w-full items-center justify-center rounded-xl bg-brand text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            :disabled="isLoading"
          >
            {{ isLoading ? tt('注册中...', '註冊中...') : tt('创建账户', '建立帳戶') }}
          </button>
        </form>

        <div class="mt-6 text-center">
          <p class="text-sm text-muted-foreground">
            {{ tt('已有账户？', '已有帳戶？') }}
            <a :href="loginHref" class="font-medium text-brand hover:underline">{{ tt('去登录', '去登入') }}</a>
          </p>
        </div>
      </div>
    </div>

    <div class="text-center mt-8">
      <a :href="getLocalePrefix() || '/'" class="text-sm text-muted-foreground hover:text-foreground">{{ tt('返回首页', '返回首頁') }}</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { authSwitchHref, rememberTunshuUser, safeInternalPath } from '../auth-login/login.ts'
import { tt, getLocalePrefix, i18nFetch } from '../../shared/i18n.ts'

interface SignupBody {
  code?: number
  reason?: string
  message?: string
  data?: {
    status?: string
    outcome?: string
    redirect?: string
    user?: { id?: string; name?: string; avatarUrl?: string }
  }
}

const loginHref = computed(() => authSwitchHref('/auth/login'))

const formData = ref({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
})
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const pendingVerification = ref(false)
const passwordIgnored = ref(false)
const emailTaken = ref(false)
const fieldErrors = ref({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
})

const passwordRequirements = computed(() => [
  {
    met: [...formData.value.password].length >= 8,
    text: tt('至少 8 个字符', '至少 8 個字元'),
  },
])

function clearFieldError(field: 'name' | 'email' | 'password' | 'confirmPassword') {
  fieldErrors.value[field] = ''
  if (errorMessage.value) {
    errorMessage.value = ''
  }
}

function resetFieldErrors() {
  fieldErrors.value.name = ''
  fieldErrors.value.email = ''
  fieldErrors.value.password = ''
  fieldErrors.value.confirmPassword = ''
}

function mapRegisterError(status: number, reason: string): void {
  emailTaken.value = false
  if (reason === 'PASSWORD_POLICY_VIOLATION') {
    errorMessage.value = tt('密码需要至少 8 个字符，且不超过 128 字节', '密碼需要至少 8 個字元，且不超過 128 位元組')
    fieldErrors.value.password = tt('密码需要至少 8 个字符，且不超过 128 字节', '密碼需要至少 8 個字元，且不超過 128 位元組')
    return
  }
  if (status >= 500 || reason === 'error.user.signup_session_not_issued') {
    errorMessage.value = tt('账号可能已经创建，请前往登录', '帳號可能已經建立，請前往登入')
    return
  }
  if (status === 409 || reason === 'EMAIL_ALREADY_REGISTERED') {
    errorMessage.value = tt('该邮箱已注册', '該郵箱已註冊')
    fieldErrors.value.email = tt('邮箱已注册', '郵箱已註冊')
    emailTaken.value = true
    return
  }
  if (status === 429) {
    errorMessage.value = tt('注册尝试次数过多。请稍后再试。', '註冊嘗試次數過多。請稍後再試。')
    return
  }
  if (status === 400) {
    errorMessage.value = tt('输入无效。请检查信息后重试。', '輸入無效。請檢查資訊後重試。')
    return
  }
  errorMessage.value = tt('注册失败。请重试。', '註冊失敗。請重試。')
}

async function handleRegister() {
  errorMessage.value = ''
  resetFieldErrors()

  if (formData.value.password !== formData.value.confirmPassword) {
    errorMessage.value = tt('两次输入的密码不一致', '兩次輸入的密碼不一致')
    fieldErrors.value.confirmPassword = tt('两次输入的密码不一致', '兩次輸入的密碼不一致')
    return
  }

  const unmetRequirements = passwordRequirements.value.filter((req) => !req.met)
  if (unmetRequirements.length > 0) {
    errorMessage.value = tt('请满足全部密码要求', '請滿足全部密碼要求')
    fieldErrors.value.password = tt('密码不符合要求', '密碼不符合要求')
    return
  }

  isLoading.value = true
  pendingVerification.value = false
  passwordIgnored.value = false
  emailTaken.value = false
  try {
    const redirect = safeInternalPath(new URLSearchParams(window.location.search).get('redirect'))
    const res = await i18nFetch('/bff/v1/webauth/signup', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        email: formData.value.email,
        password: formData.value.password,
        username: formData.value.name,
        redirect,
      }),
    })

    const body = (await res.json().catch(() => ({}))) as SignupBody
    if (!res.ok || (typeof body.code === 'number' && body.code !== 0)) {
      console.error('[Register] signup failed', res.status, body.code, body.reason)
      mapRegisterError(res.status, body.reason || '')
      return
    }

    if (body.data?.status === 'pending_verification') {
      pendingVerification.value = true
      passwordIgnored.value = body.data.outcome === 'verification_resent'
      return
    }
    if (body.data?.status === 'active') {
      rememberTunshuUser(body.data.user)
      sessionStorage.setItem('tunshu.auth.registered', '1')
      window.location.assign(safeInternalPath(body.data.redirect || redirect))
      return
    }
    errorMessage.value = tt('注册没有完成。若账号已创建，请前往登录', '註冊沒有完成。若帳號已建立，請前往登入')
  } catch (error: unknown) {
    console.error('[Register] handleRegister error:', error)
    errorMessage.value = tt('注册失败。请重试。', '註冊失敗。請重試。')
  } finally {
    isLoading.value = false
  }
}
</script>
